import type * as T from "@src/types";
import type { ApiCandle, ApiSymbol } from "@tradingdroid/frontends-types";

const supportedResolutions = [
  "1",
  "3",
  "5",
  "15",
  "30",
  "60",
  "120",
  "240",
  // '360',
  "720",
  "12H",
  "D",
  // 'W',
  // 'M',
] as T.TV.ResolutionString[];

export const configurationData: T.TV.DatafeedConfiguration = {
  supported_resolutions: supportedResolutions,
  supports_marks: true,
  supports_timescale_marks: true,
};

export const mapApiToTradingviewSymbol = (apiSymbol: ApiSymbol) => ({
  symbol: apiSymbol.name,
  full_name: `${apiSymbol.exchange}:${apiSymbol.name}`,
  description: `${apiSymbol.base}/${apiSymbol.quote}`,
  exchange: apiSymbol.exchange,
  ticker: `${apiSymbol.exchange}:${apiSymbol.name}`,
  type: apiSymbol.kind,
});

/**
 * Convert TradingView resolution to API period format (e.g., "60" → "1h").
 *
 * TradingView is inconsistent about day and week resolutions: it accepts "D" but hands back "1D"
 * from the chart, so both spellings have to be here. Missing "1D" silently fell through to the 5m
 * fallback, which requested five-minute candles for a chart drawn in days and rendered a handful
 * of bars smeared across a year.
 */
export const tvResolutionToApiPeriod = (resolution: string): string => {
  const mapping: Record<string, string> = {
    "1": "1m",
    "3": "3m",
    "5": "5m",
    "15": "15m",
    "30": "30m",
    "60": "1h",
    "120": "2h",
    "240": "4h",
    "720": "12h",
    "12H": "12h",
    D: "1d",
    "1D": "1d",
    W: "1w",
    "1W": "1w",
  };
  const period = mapping[resolution];
  if (!period) {
    console.warn(
      `[tvResolutionToApiPeriod] Unknown resolution "${resolution}", falling back to 5m`,
    );
    return "5m";
  }
  return period;
};

/** Minutes in an API period, used to size a candle request to the range it covers. */
export const apiPeriodToMinutes = (period: string): number => {
  const match = /^(\d+)([mhdw])$/.exec(period);
  if (!match) {
    return 0;
  }
  const value = Number(match[1]);
  const unit = { m: 1, h: 60, d: 1440, w: 10080 }[match[2] as "m" | "h" | "d" | "w"];
  return value * unit;
};

/** Convert API candles to TradingView Bar format */
export const apiCandlesToBars = (candles: ApiCandle[]): T.TV.Bar[] => {
  return candles
    .map((c) => ({
      time: new Date(c.open_time).getTime(),
      open: c.open,
      high: c.high,
      low: c.low,
      close: c.close,
      volume: c.volume,
    }))
    .sort((a, b) => a.time - b.time);
};

export const binarySearch = (
  arr: Uint32Array,
  target: number,
  start: number = 0,
  end: number = arr.length - 1,
): number => {
  let left = start,
    right = end;

  while (left <= right) {
    const mid = Math.floor((left + right) / 2);
    const midVal = arr[mid];

    if (midVal === target) return mid;
    if (midVal > target) {
      left = mid + 1; // Our value is too new, look in older half
    } else {
      right = mid - 1; // Our value is too old, look in newer half
    }
  }

  return left;
};
